import api from "./axios";
import ApiResponse from "../responses/ApiResponse";
import SignupRequest from "../requests/SingupRequest";
import SigninRequest from "../requests/SigninRequest";
import SigninResponse from "../responses/SigninResponse";
import SignupResponse from "../responses/SignupResponse";
import DeepLinkRequest from "../requests/DeepLinkRequest";
import DeepLinkResponse from "../responses/DeepLinkResponse";
import ValidateResetPasswordLinkRequest from "../requests/ValidateResetPasswordLinkRequest";
import ResetPasswordRequest from "../requests/ResetPasswordRequest";
import ChangePasswordRequest from "../requests/ChangePasswordRequest";
import ChangePasswordResponse from "../responses/ChangePasswordResponse";
import ValidatePasswordRequest from "../requests/ValidatePasswordRequest";
import AccountActivationStatusResponse from "../responses/AccountActivationStatusResponse";
import ActivateAccountRequest from "../requests/ActivateAccountRequest";
import ValidateAuthenticationResponse from "../responses/ValidateAuthenticationResponse";

class AuthenticationService {
  async signup(signupRequest: SignupRequest) {
    try {
      const response = await api.post(
        "/authentication/register",
        signupRequest
      );
      return response.data as SignupResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error("There was an error signing up: " + error.message);
      }
    }
  }

  async signin(signinRequest: SigninRequest) {
    try {
      const response = await api.post("/authentication/login", signinRequest);
      return response.data as SigninResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error("There was an error signing in: " + error.message);
      }
    }
  }

  async signout() {
    try {
      await api.post("/authentication/logout");
    } catch (error) {
      if (error instanceof Error) {
        throw new Error("There was an error logging out: " + error.message);
      }
    }
  }

  async deepLinkSignin(deeplinkRequest: DeepLinkRequest) {
    try {
      const response = await api.post(
        "/authentication/deep-link-login",
        deeplinkRequest
      );
      return response.data as DeepLinkResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error("There was an error signing in: " + error.message);
      }
    }
  }

  async isAuthenticated() {
    const response = await api.get("/authentication/validate-authentication");
    return response.data as ValidateAuthenticationResponse;
  }

  async validateResetPasswordLink(request: ValidateResetPasswordLinkRequest) {
    try {
      const response = await api.post(
        "/authentication/reset-password/validate",
        request
      );
      return response.data as ApiResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error validating the reset password link: " +
            error.message
        );
      }
    }
  }

  async resetPassword(request: ResetPasswordRequest) {
    try {
      const response = await api.post(
        "/authentication/reset-password",
        request
      );
      return response.data as ApiResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error resetting the password: " + error.message
        );
      }
    }
  }

  async changePassword(request: ChangePasswordRequest) {
    try {
      const response = await api.post(
        "/authentication/change-password",
        request
      );
      return response.data as ChangePasswordResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error changing the password: " + error.message
        );
      }
    }
  }

  async isFullyAuthenticated() {
    const response = await api.get(
      "/authentication/validate-full-authentication-status"
    );
    return response.data;
  }

  async validateConfirmPassword(request: ValidatePasswordRequest) {
    const response = await api.post(
      "/authentication/validate-password",
      request
    );

    return response.data;
  }

  async checkAccountActivationStatus(
    applicationName: string,
    clubId: number,
    hashedEmail: string,
    emailVerificationCode: string
  ) {
    if (
      !applicationName ||
      clubId < 0 ||
      !hashedEmail ||
      !emailVerificationCode
    ) {
      throw new Error("Invalid request parameters.");
    }

    const url = `/authentication/${applicationName}/${clubId}/activation-status`;

    try {
      const response = await api.get(url, {
        params: {
          hashedEmail,
          emailVerificationCode,
        },
      });

      return response.data as AccountActivationStatusResponse;
    } catch (error: any) {
      console.error("Activation status check failed:", error);
      throw error;
    }
  }

  async activateAccount(request: ActivateAccountRequest){
    try {
      const response = await api.post(
        "/authentication/activate-account",
        request,
        { skipGlobal500Handler: true }  // use this to stop the global 500 handler from redirecting to the 500 error page and show the local error message
      );
      return response.data as AccountActivationStatusResponse;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          "There was an error activating the account: " + error.message
        );
      }
    }
  }
}

export default new AuthenticationService();
